/**
 * Resolve ONE client by id, through the list endpoint's `clientId` filter.
 *
 * Shared by the picker (which needs the selected client's name) and by
 * any form deriving policy from the selection (the create-student page
 * reads `isInternal` to decide whether the operator-password field
 * exists). One hook, one query key, so those are one request rather than
 * two — and, more importantly, so they cannot disagree about which
 * client is selected.
 *
 * It replaces a hand-synced `{ limit: 200 }` params literal that the
 * create-student page kept byte-identical to the picker's in order to
 * observe the same cache entry. That worked until the picker's params
 * changed, which is exactly what PROV-SCALE did.
 */
import { useQuery } from '@tanstack/react-query'

import { queryKeys } from '../api/query-keys'
import { adminClientsApi } from './api'
import type { ClientRow, ListClientsParams } from './types'

/** The by-id lookup's params. Exported so callers can seed its cache. */
export function clientByIdParams(clientId: string): ListClientsParams {
  return { clientIds: [clientId] }
}

/**
 * `data` is the client, or `null` once the lookup resolved and found
 * nothing (a soft-deleted tenant an old link still points at). A failed
 * request leaves `data` undefined with `isError` set — the caller
 * decides whether that degrades a control or blocks it.
 */
export function useClient(clientId: string) {
  const params = clientByIdParams(clientId)
  return useQuery({
    queryKey: queryKeys.clientList(params),
    queryFn: () => adminClientsApi.list(params),
    enabled: clientId !== '',
    select: (data): ClientRow | null => data.clients[0] ?? null,
  })
}
